import { ColDef, ITooltipParams } from "ag-grid-community"
import { useMemo } from "react"
import MetricCellRenderer from "../components/MetricCellRenderer"
import TableTooltip from "../components/TableTooltip"

const LABEL_ORDER = [
  "Error/Last",
  "Error/Range",
  "Median Error",
  "Scale Error",
  "Avg Worst Dev",
  "Overshoot Rate",
  "Sign Accuracy",
  "Start Date",
  "End Date",
]

const LABEL_MAP: Record<string, string> = {
  error_on_last: "Error/Last",
  error_on_range: "Error/Range",
  median_error: "Median Error",
  scale_error: "Scale Error",
  avg_worst_dev: "Avg Worst Dev",
  overshoot_rate: "Overshoot Rate",
  sign_accuracy: "Sign Accuracy",
  starting_backtest_date: "Start Date",
  end_backtest_date: "End Date",
}

const fmt = (v: any, decimals = 2) => (typeof v === "number" ? v.toFixed(decimals) : v ?? null)

const fmtPct = (v: any) => (typeof v === "number" ? `${v.toFixed(2)}%` : v ?? null)

/**
 * Tooltip value getter for the metric column - formats backtest metrics for display
 */
const getMetricTooltipValue = (params: ITooltipParams): string | null => {
  if (!params?.node || !params?.data) {
    return null
  }

  const rowData = params.data
  const tooltipData = rowData?._tooltip_metrics
  if (!tooltipData || !tooltipData.metrics || tooltipData.metrics.length === 0) {
    return null
  }

  const formattedMetrics = tooltipData.metrics
    .map((m: any) => {
      const cleanLabel = LABEL_MAP[m.label] || m.label
      const isPct =
        cleanLabel.includes("Error/") ||
        cleanLabel.includes("Overshoot") ||
        cleanLabel.includes("Sign Accuracy")
      const formatter = isPct ? fmtPct : fmt
      return {
        label: cleanLabel,
        baseline: formatter(m.baseline),
        adjusted: formatter(m.adjusted),
        baseline3p: formatter(m.baseline3p),
        adjusted3p: formatter(m.adjusted3p),
        baseline9p: formatter(m.baseline9p),
        adjusted9p: formatter(m.adjusted9p),
      }
    })
    .sort(
      (a: { label: string }, b: { label: string }) =>
        LABEL_ORDER.indexOf(a.label) - LABEL_ORDER.indexOf(b.label),
    )

  return JSON.stringify({ metrics: formattedMetrics })
}

/**
 * Tooltip value getter for date columns - shows prediction and error data
 */
const getDateColumnTooltipValue = (params: ITooltipParams): string | null => {
  if (!params?.data || !params?.colDef || !("field" in params.colDef) || !params.colDef.field) return null

  const rowData = params.data
  const fieldName = String(params.colDef.field)

  const dateMatch = fieldName.match(/^(\d{4}-\d{2}-\d{2})/)
  const cellDate = dateMatch ? dateMatch[1] : null
  if (!cellDate) return null

  const parts: string[] = []

  try {
    const predBaseline = rowData._baseline_data_with_baseline_prediction_per_date
      ? JSON.parse(rowData._baseline_data_with_baseline_prediction_per_date)?.[cellDate]
      : null

    const predAdjusted = rowData._adjusted_data_per_date
      ? JSON.parse(rowData._adjusted_data_per_date)?.[cellDate]
      : null

    const errorLast = rowData._error_on_last_per_date
      ? JSON.parse(rowData._error_on_last_per_date)?.[cellDate]
      : null

    const errorRange = rowData._error_on_range_per_date
      ? JSON.parse(rowData._error_on_range_per_date)?.[cellDate]
      : null

    if (typeof predBaseline === "number") {
      parts.push(`Pred Baseline: ${predBaseline.toFixed(2)}`)
    }

    if (typeof predAdjusted === "number") {
      parts.push(`Pred Adjusted: ${predAdjusted.toFixed(2)}`)
    }

    if (typeof errorLast === "number") {
      parts.push(`Error/Last: ${errorLast.toFixed(2)}%`)
    }

    if (typeof errorRange === "number") {
      parts.push(`Error/Range: ${errorRange.toFixed(2)}%`)
    }
  } catch {
    return null
  }

  return parts.length > 0 ? parts.join(" | ") : null
}

/**
 * Hook to transform column definitions with proper widths and tooltip getters
 */
export const useDatavizColumns = (columnDefs: ColDef[]): ColDef[] => {
  return useMemo(() => {
    const datePrefixRe = /^(\d{4}-\d{2}-\d{2})/

    return columnDefs.map(column => {
      if ("field" in column && column.field === "metric") {
        return {
          ...column,
          width: 500,
          cellRenderer: MetricCellRenderer,
          tooltipComponent: TableTooltip,
          tooltipShowDelay: 0,
          tooltipValueGetter: getMetricTooltipValue,
        }
      }

      if ("field" in column && column.field !== "remove") {
        const colField = String(column.field)
        const isDateColumn = datePrefixRe.test(colField)

        const updatedColumn: ColDef = {
          ...column,
          width: 100,
        }

        if (isDateColumn) {
          updatedColumn.tooltipValueGetter = getDateColumnTooltipValue
        }

        return updatedColumn
      }

      return column
    })
  }, [columnDefs])
}
